import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
/* Toaster is untyped JS (allowJs). */
import { toasterMsg } from '../../../../../../Components/Toaster/Toaster';
import { rowKeyOf, useMeasurementsDispatch, useMeasurementsState } from './MeasurementsProvider';
import {
  clearHighlights as clearHighlightsRequest,
  getMeasurements,
  getSupportingDocs,
  saveMeasurements,
  validateGrading,
} from './measurements.service';
import type { BulkSaveProduct, MeasurementProduct, SpecPomItem } from './types';

/** Products fetched per Load-More page (BM-9210: paginate large selections instead of loading all). */
const PAGE_SIZE = 20;

/** Minimal shape of the slice holding the logged-in user's grading format. */
interface UserRootState {
  UserDetails?: { userInfo?: { gradingFormat?: string } };
}

/**
 * Send every size `measurment` as a string. The sample-size engine derives values with `round3`, which
 * yields a Number, while the previous-size engine formats to a String — this normalises both so the
 * stored measurement type never depends on the user's grading format. `null` and absent values are left
 * untouched so an empty measurement is not written as the string "null".
 */
function withStringMeasurements(item: SpecPomItem): SpecPomItem {
  const gradings = item.gradings ?? [];
  return {
    ...item,
    gradings: gradings.map((grading) =>
      grading.measurment === null || grading.measurment === undefined ? grading : { ...grading, measurment: String(grading.measurment) },
    ),
  };
}

/**
 * Row keys whose POM Code is repeated within the product. Push matches targets on an exact code, so a
 * duplicate makes the destination ambiguous — save rejects it rather than letting the data reach the
 * database. Compared raw (no trimming/case-folding) to match the push comparison exactly. Scans every
 * row of the product, not only the changed ones, so a duplicate already present is still caught.
 */
export function duplicateCodeRowKeys(product: MeasurementProduct): string[] {
  const countByCode = new Map<string, number>();
  product.specPomLibraries.forEach((item) => {
    const code = item.code ?? '';
    if (code) countByCode.set(code, (countByCode.get(code) ?? 0) + 1);
  });
  return product.specPomLibraries
    .map((item, index) => ({ code: item.code ?? '', rowKey: rowKeyOf(item, index) }))
    .filter(({ code }) => (countByCode.get(code) ?? 0) > 1)
    .map(({ rowKey }) => rowKey);
}

/** Map a product to the save payload carrying ONLY its changed rows plus the full row-key membership. */
export function toChangedPayload(product: MeasurementProduct, changedSet: Set<string>): BulkSaveProduct {
  const rowKeys = product.specPomLibraries.map((item, index) => rowKeyOf(item, index));
  const changedRows = product.specPomLibraries
    .map((item, index) => ({ rowKey: rowKeyOf(item, index), item }))
    .filter(({ rowKey }) => changedSet.has(`${product.devOrderId}::${rowKey}`))
    .map(({ rowKey, item }) => ({ rowKey, item: withStringMeasurements(item) }));
  return { devOrderId: product.devOrderId, rowKeys, changedRows };
}

export interface UseMeasurementsData {
  proceedAfterConfirm: () => void;
  save: () => Promise<void>;
  clearHighlights: () => Promise<void>;
  loadMore: () => Promise<void>;
}

/**
 * Drives the Measurements module data flow:
 *  validate grading (once) → if ineligible products, wait on the modal → load measurements
 *  + supporting docs. Also exposes save-all and clear-highlights for the floating actions.
 */
export function useMeasurementsData(productIds: string[]): UseMeasurementsData {
  const { t } = useTranslation('bulk-edit');
  const state = useMeasurementsState();
  const dispatch = useMeasurementsDispatch();
  const gradingFormat = useSelector((root: UserRootState) => root.UserDetails?.userInfo?.gradingFormat ?? '');
  const validatedRef = useRef(false);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);

  /* Validate grading once for the selected products. */
  useEffect(() => {
    if (validatedRef.current || productIds.length === 0) return;
    validatedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const result = await validateGrading(productIds);
        if (!cancelled) dispatch({ type: 'VALIDATED', payload: result });
      } catch (error) {
        if (!cancelled) dispatch({ type: 'ERROR', payload: error instanceof Error ? error.message : t('MEASUREMENTS_MODULE.VALIDATION_FAILED') });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productIds, dispatch, t]);

  /* Load measurements + supporting docs once we have an eligible cohort and a grading group. */
  useEffect(() => {
    if (state.phase !== 'loading' || !state.gradingGroupId || state.eligibleProductIds.length === 0) return;
    if (loadingRef.current) return;
    loadingRef.current = true;
    const { gradingGroupId, eligibleProductIds } = state;
    /* Send the full cohort + page 1; the server returns the first page. Further pages arrive via loadMore. */
    let cancelled = false;
    (async () => {
      try {
        const [data, supportingDocs] = await Promise.all([
          getMeasurements(eligibleProductIds, gradingGroupId, 1, PAGE_SIZE),
          getSupportingDocs(eligibleProductIds, gradingGroupId, 1, PAGE_SIZE),
        ]);
        if (!cancelled) dispatch({ type: 'LOADED', payload: { data, supportingDocs, gradingFormat } });
      } catch (error) {
        if (!cancelled) dispatch({ type: 'ERROR', payload: error instanceof Error ? error.message : t('MEASUREMENTS_MODULE.LOAD_FAILED') });
      } finally {
        loadingRef.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state.phase, state.gradingGroupId, state.eligibleProductIds, gradingFormat, dispatch, t]);

  const proceedAfterConfirm = useCallback(() => {
    dispatch({ type: 'LOADING' });
  }, [dispatch]);

  const save = useCallback(async () => {
    if (!state.gradingGroupId || state.products.length === 0) return;
    const changedSet = new Set(state.changedRowIds);
    const isChangedRow = (devOrderId: string, rowKey: string): boolean => changedSet.has(`${devOrderId}::${rowKey}`);
    const changedProducts = state.products.filter((product) =>
      product.specPomLibraries.some((item, index) => isChangedRow(product.devOrderId, rowKeyOf(item, index))),
    );
    if (changedProducts.length === 0) {
      dispatch({ type: 'SAVED' });
      return;
    }

    /* Required-field validation: every changed POM row must have a non-empty Code and Description. */
    const invalidCells: string[] = [];
    let scrollTo: { rowId: string; colId: string } | null = null;
    for (const product of changedProducts) {
      product.specPomLibraries.forEach((item, index) => {
        const rowKey = rowKeyOf(item, index);
        if (!isChangedRow(product.devOrderId, rowKey)) return;
        const rowId = `${product.devOrderId}::${rowKey}`;
        if (!String(item.code ?? '').trim()) {
          invalidCells.push(`${rowId}::code`);
          if (!scrollTo) scrollTo = { rowId, colId: 'code' };
        }
        if (!String(item.description ?? '').trim()) {
          invalidCells.push(`${rowId}::description`);
          if (!scrollTo) scrollTo = { rowId, colId: 'description' };
        }
      });
    }
    if (invalidCells.length > 0) {
      dispatch({ type: 'SET_INVALID_CELLS', payload: { invalidCells, scrollTo } });
      toasterMsg(t('MEASUREMENTS_MODULE.REQUIRED_CODE_DESC'), 'danger');
      return;
    }

    /* Uniqueness validation: a product may not carry the same POM Code twice (Push matches on code). */
    const duplicateCells: string[] = [];
    let duplicateScrollTo: { rowId: string; colId: string } | null = null;
    for (const product of changedProducts) {
      duplicateCodeRowKeys(product).forEach((rowKey) => {
        const rowId = `${product.devOrderId}::${rowKey}`;
        duplicateCells.push(`${rowId}::code`);
        if (!duplicateScrollTo) duplicateScrollTo = { rowId, colId: 'code' };
      });
    }
    if (duplicateCells.length > 0) {
      dispatch({ type: 'SET_INVALID_CELLS', payload: { invalidCells: duplicateCells, scrollTo: duplicateScrollTo } });
      toasterMsg(t('MEASUREMENTS_MODULE.DUPLICATE_POM_CODE'), 'danger');
      return;
    }

    try {
      const payload = changedProducts.map((product) => toChangedPayload(product, changedSet));
      await saveMeasurements(state.gradingGroupId, payload);
      dispatch({ type: 'SET_INVALID_CELLS', payload: { invalidCells: [], scrollTo: null } });
      dispatch({ type: 'SAVED' });
      toasterMsg(t('MEASUREMENTS_MODULE.SAVED'), 'success');
    } catch (error) {
      toasterMsg(error instanceof Error ? error.message : t('MEASUREMENTS_MODULE.SAVE_FAILED'), 'danger');
    }
  }, [state.gradingGroupId, state.products, state.changedRowIds, dispatch, t]);

  const loadMore = useCallback(async () => {
    const { gradingGroupId, eligibleProductIds, products } = state;
    /* Guard on a ref, not state.loadingMore: two scroll triggers can both read the stale
       render closure before the LOADING_MORE dispatch commits and append the same page twice. */
    if (loadingMoreRef.current || !gradingGroupId) return;
    if (products.length >= eligibleProductIds.length) return;
    loadingMoreRef.current = true;
    /* Next 1-based page over the full cohort — the server slices to this page. */
    const pageIndex = Math.floor(products.length / PAGE_SIZE) + 1;
    dispatch({ type: 'LOADING_MORE' });
    try {
      const [data, supportingDocs] = await Promise.all([
        getMeasurements(eligibleProductIds, gradingGroupId, pageIndex, PAGE_SIZE),
        getSupportingDocs(eligibleProductIds, gradingGroupId, pageIndex, PAGE_SIZE),
      ]);
      dispatch({ type: 'LOADED_MORE', payload: { data, supportingDocs } });
    } catch (error) {
      dispatch({ type: 'LOAD_MORE_ERROR' });
      toasterMsg(error instanceof Error ? error.message : t('MEASUREMENTS_MODULE.LOAD_MORE_FAILED'), 'danger');
    } finally {
      loadingMoreRef.current = false;
    }
  }, [state, dispatch, t]);

  const clearHighlights = useCallback(async () => {
    if (!state.gradingGroupId || state.eligibleProductIds.length === 0) return;
    try {
      await clearHighlightsRequest(state.eligibleProductIds, state.gradingGroupId);
      dispatch({ type: 'CLEARED_HIGHLIGHTS' });
      toasterMsg(t('MEASUREMENTS_MODULE.HIGHLIGHTS_CLEARED'), 'success');
    } catch (error) {
      toasterMsg(error instanceof Error ? error.message : t('MEASUREMENTS_MODULE.CLEAR_HIGHLIGHTS_FAILED'), 'danger');
    }
  }, [state.gradingGroupId, state.eligibleProductIds, dispatch, t]);

  /* Push is not here — it is owned by `cells/PushHeaderAction`, mounted in the Spec Table group header. */
  return { proceedAfterConfirm, save, clearHighlights, loadMore };
}
